'use client';
import classnames from 'classnames/bind';
import { useLocale, useTranslations } from 'next-intl';
import { memo, useEffect, useState } from 'react';
import {
  BasicClearIcon,
  ButtonInterface,
  Drawer,
  Preloader,
  Text,
} from '@portals/ui-kit';
import { DataParser, TSerializedEditorState } from '@portals/hc-rich-editor';
import { ECountry } from '@portals/help-center-core';
import { useClientEnvContext } from '@/contexts';
import { getArticleMaterialRequest } from '@/api/requests/get-article-material';
import { getArticleOneRequest } from '@/api/requests/get-article-one';
import { ERROR_TRANSLATIONS } from '@/_components/success-stories/_constants';
import { instructionArticleUrl } from '@/api/endpoints/article-one';
import styles from './index.module.scss';

const cn = classnames.bind(styles);
const BLOCK_NAME = 'ArticleContent';

type TProps = {
  articleId: string;
  isOpen: boolean;
  onClose: () => void;
};

export const ArticleContent = memo(function ArticleContent({
  articleId,
  isOpen,
  onClose,
}: TProps) {
  const [articleTitle, setArticleTitle] = useState<string>('');
  const [materialItem, setMaterial] = useState<TSerializedEditorState | null>();
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const locale = useLocale();
  const translations = useTranslations();
  const { NEXT_PUBLIC_INSTRUCTION_URL } = useClientEnvContext();

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        setHasError(false);
        const response = await getArticleOneRequest({
          articleId,
          language: locale,
          country: ECountry.RUSSIA,
          endpoint: `${NEXT_PUBLIC_INSTRUCTION_URL}${instructionArticleUrl}`,
        });

        const { data, error, errorText, additionalErrors } = response;
        const { article: material } = data || {};

        if (error) {
          if (additionalErrors && additionalErrors.errors) {
            throw new Error(JSON.stringify(additionalErrors.errors));
          }

          throw new Error(errorText);
        }

        setArticleTitle(material.title);

        const responseArticleMaterial = await getArticleMaterialRequest({
          contentUrl: material?.contentUrl || '',
        });

        const {
          data: materialContentResponse,
          error: materialContentResponseError,
          errorText: materialContentResponseErrorText,
        } = responseArticleMaterial;

        setMaterial(materialContentResponse?.reachEditorState);

        if (materialContentResponseError) {
          throw new Error(materialContentResponseErrorText);
        }

        return data;
      } catch (error) {
        console.error('fetch articles get an error', error);
        setHasError(true);
        setMaterial(null);
      } finally {
        setIsLoading(false);
      }
    };

    if (isOpen && articleId) {
      fetchData();
    }
  }, [articleId, isOpen]);

  return (
    <Drawer
      dataTestid="success-modal"
      isOpened={isOpen}
      onClose={onClose}
      radius="md"
      size="min-content"
      type="dialog"
      offset="md"
      placement="center"
      portalPrefix="deactivate-modal"
      closeButtonPlacement="top"
      heightBehavior="content"
      isShowCloseButton
    >
      <div className={cn(BLOCK_NAME)}>
        <div className={cn(`${BLOCK_NAME}__header`)}>
          <div className={cn(`${BLOCK_NAME}__header--button`)}>
            <ButtonInterface
              leftIcon={BasicClearIcon}
              modifier="circle"
              onClick={onClose}
              size="s"
            />
          </div>
        </div>
        <div className={cn(`${BLOCK_NAME}__content`)}>
          {isLoading && !materialItem && (
            <div className={cn(`${BLOCK_NAME}__preloader`)}>
              <Preloader color="dark-purple" size="medium" />
            </div>
          )}
          {hasError && (
            <div className={cn(`${BLOCK_NAME}__error`)}>
              <Text
                size="headline-s-bold"
                text={translations(ERROR_TRANSLATIONS.title)}
              />
              <Text
                size="body-l"
                text={translations(ERROR_TRANSLATIONS.description)}
                color="grey700"
              />
            </div>
          )}
          {materialItem && (
            <div className={cn(`${BLOCK_NAME}__client-article`)}>
              <Text size="headline-s" color="grey900" text={articleTitle} />
              <DataParser data={materialItem} />
            </div>
          )}
        </div>
      </div>
    </Drawer>
  );
});
